import { UnorderedListOutlined } from '@ant-design/icons';
import {
  type IAdminWithdrawalDetail,
  type IAdminWithdrawalEmail,
  type IAdminWithdrawalItem,
  type IAdminWithdrawalOtherForOrder,
} from '@mahawi/eshop-common/src/types';
import {
  Descriptions,
  Divider,
  Result,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import LinkButton from '../components/link-button';
import StatusTag from '../components/status-tag';
import TableComponent from '../components/table';
import CancelOrderPanel, {
  canCancelWithdrawalOrder,
} from '../components/withdrawal/cancel-order-panel';
import HistoryTable from '../components/withdrawal/history-table';
import {
  WITHDRAWAL_CHANNEL_LABEL,
  WITHDRAWAL_ENTRY_POINT_LABEL,
  WITHDRAWAL_ORDER_LINK_SOURCE_LABEL,
} from '../components/withdrawal/labels';
import LinkOrderPanel from '../components/withdrawal/link-order-panel';
import RefundDue from '../components/withdrawal/refund-due';
import StatusForm from '../components/withdrawal/status-form';
import WithdrawalStatusTag from '../components/withdrawal/status-tag';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { withdrawalLoad } from '../reducers/withdrawal/reducer';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

type TEmailRow = IAdminWithdrawalEmail & { key: string };
type TOtherRow = IAdminWithdrawalOtherForOrder & { key: string };

const formatDateTime = (value: string): string =>
  `${dayjs(value).format(EDateFormat.DATE_TIME)} (${new Date(value).toISOString()})`;

const emailColumns: ColumnsType<TEmailRow> = [
  {
    title: 'Sent',
    dataIndex: 'sentAt',
    render: (sentAt: string): string =>
      dayjs(sentAt).format(EDateFormat.DATE_TIME),
  },
  { title: 'Type', dataIndex: 'emailTypeCode' },
  {
    title: 'Recipients',
    dataIndex: 'recipients',
    render: (recipients: string[]): string => recipients.join(', '),
  },
];

const otherColumns: ColumnsType<TOtherRow> = [
  {
    title: 'Reference',
    dataIndex: 'referenceCode',
    render: (referenceCode: string, record: TOtherRow): React.ReactElement => (
      <Link to={`/orders/withdrawal/${record.uuid}`} label={referenceCode} />
    ),
  },
  {
    title: 'Received',
    dataIndex: 'receivedAt',
    render: (receivedAt: string): string =>
      dayjs(receivedAt).format(EDateFormat.DATE_TIME),
  },
  {
    title: 'Status',
    render: (_value: string, record: TOtherRow): React.ReactElement => (
      <WithdrawalStatusTag status={record.status} />
    ),
  },
];

const textOrDash = (value: string | null): string => value ?? EMPTY_VALUE;

const renderItems = (items: IAdminWithdrawalItem[]): React.ReactNode =>
  items.length === 0 ? (
    EMPTY_VALUE
  ) : (
    <Space orientation="vertical" size={SPACING.xs}>
      {items.map((item: IAdminWithdrawalItem) => (
        <Text key={item.productUuid}>{`${item.count} × ${item.name}`}</Text>
      ))}
    </Space>
  );

function SubmittedContent({
  detail,
}: {
  detail: IAdminWithdrawalDetail;
}): React.ReactElement {
  return (
    <Descriptions
      bordered
      size="small"
      column={1}
      items={[
        {
          key: 'receivedAt',
          label: 'Received',
          children: formatDateTime(detail.receivedAt),
        },
        {
          key: 'createdAt',
          label: 'Recorded',
          children: formatDateTime(detail.createdAt),
        },
        {
          key: 'channel',
          label: 'Channel',
          children: (
            <StatusTag
              status={detail.channel}
              label={WITHDRAWAL_CHANNEL_LABEL[detail.channel]}
            />
          ),
        },
        {
          key: 'entryPoint',
          label: 'Entry point',
          children: detail.entryPoint ? (
            <StatusTag
              status={detail.entryPoint}
              label={WITHDRAWAL_ENTRY_POINT_LABEL[detail.entryPoint]}
            />
          ) : (
            EMPTY_VALUE
          ),
        },
        {
          key: 'recordedBy',
          label: 'Recorded by',
          children: detail.recordedBy?.email ?? 'Customer (online form)',
        },
        { key: 'language', label: 'Language', children: detail.languageCode },
        { key: 'name', label: 'Name', children: detail.name },
        {
          key: 'orderNumber',
          label: 'Order number',
          children: detail.orderNumber,
        },
        {
          key: 'orderEmail',
          label: 'Order email',
          children: detail.orderEmail,
        },
        {
          key: 'confirmationEmail',
          label: 'Confirmation email',
          children: detail.confirmationEmail,
        },
        {
          key: 'reason',
          label: 'Reason',
          children: textOrDash(detail.reason),
        },
        {
          key: 'itemsDescription',
          label: 'Items description',
          children: textOrDash(detail.itemsDescription),
        },
        { key: 'items', label: 'Items', children: renderItems(detail.items) },
      ]}
    />
  );
}

function WithdrawalContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const { uuid } = useParams();
  const detail = useSelector((s: RootState) => s.Withdrawal.detail);
  const inProcess = useSelector((s: RootState) => s.Withdrawal.inProcess);

  useEffect((): void => {
    if (uuid) {
      dispatch(withdrawalLoad(uuid));
    }
  }, [dispatch, uuid]);

  if (detail === null) {
    return (
      <Result
        status="404"
        title="Withdrawal not found"
        subTitle={`Withdrawal with uuid ${uuid ?? ''} not found.`}
        extra={<Link to="/orders/withdrawals" label="Back to withdrawals" />}
      />
    );
  }

  if (!detail || detail.uuid !== uuid) {
    return <Skeleton active />;
  }

  return (
    <>
      <DetailPageHeader
        title={`Withdrawal ${detail.referenceCode}`}
        onReload={(): void => {
          dispatch(withdrawalLoad(detail.uuid));
        }}
        isReloadDisabled={inProcess}
        tags={<WithdrawalStatusTag status={detail.status} />}
        uuid={detail.uuid}
        links={
          <LinkButton
            to="/orders/withdrawals"
            label="All withdrawals"
            icon={<UnorderedListOutlined />}
            isIconOnly
          />
        }
      />

      <Divider titlePlacement="left">Submitted content</Divider>
      <SubmittedContent detail={detail} />

      <Divider titlePlacement="left">Order</Divider>
      {detail.orderLinkSource && (
        <div>
          <StatusTag
            status={detail.orderLinkSource}
            label={WITHDRAWAL_ORDER_LINK_SOURCE_LABEL[detail.orderLinkSource]}
          />
        </div>
      )}
      <LinkOrderPanel detail={detail} inProcess={inProcess} />

      {canCancelWithdrawalOrder(detail) && (
        <>
          <Divider titlePlacement="left">Cancel unpaid order</Divider>
          <CancelOrderPanel detail={detail} inProcess={inProcess} />
        </>
      )}

      <Divider titlePlacement="left">Status</Divider>
      <Space
        size={SPACING.sm}
        align="center"
        style={{ marginBottom: SPACING.md }}
      >
        <Text>Refund due</Text>
        <RefundDue status={detail.status} refundDueAt={detail.refundDueAt} />
      </Space>
      <StatusForm detail={detail} inProcess={inProcess} />

      <Divider titlePlacement="left">History</Divider>
      <HistoryTable
        history={detail.history}
        currencyIsoCode={detail.currencyIsoCode}
      />

      <Divider titlePlacement="left">Emails</Divider>
      <TableComponent
        columns={
          emailColumns as Parameters<typeof TableComponent>[0]['columns']
        }
        dataSource={detail.emails.map(
          (email: IAdminWithdrawalEmail, index: number) => ({
            key: `${email.sentAt}-${index}`,
            ...email,
          }),
        )}
        isLoading={false}
        emptyText="No email sent yet"
      />

      {detail.otherWithdrawalsForOrder.length > 0 && (
        <>
          <Divider titlePlacement="left">
            Other withdrawals for this order
          </Divider>
          <TableComponent
            columns={
              otherColumns as Parameters<typeof TableComponent>[0]['columns']
            }
            dataSource={detail.otherWithdrawalsForOrder.map(
              (other: IAdminWithdrawalOtherForOrder) => ({
                key: other.uuid,
                ...other,
              }),
            )}
            isLoading={false}
          />
        </>
      )}
    </>
  );
}

export default WithdrawalContainer;
